import useTranslation from "@babackapp/translations/use-translation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import FormInputContainer from "./form-input-container";

type FormSimpleCronProps = {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
};

const HOUR = 1;
const DAY = 2;
const MONTH = 3;
const DAY_OF_WEEK = 4;

type ValidHourNumber =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12"
  | "13"
  | "14"
  | "15"
  | "16"
  | "17"
  | "18"
  | "19"
  | "20"
  | "21"
  | "22"
  | "23"
  | "24";

export default function FormSimpleCron({
  label,
  description,
  value = "* * * * *",
  onChange,
}: FormSimpleCronProps) {
  const state = value.split(" ");
  const month = state[MONTH];
  const hour = state[HOUR];
  const monthDay = state[DAY];
  const weekDay = state[DAY_OF_WEEK];

  const isEveryDay = monthDay === "*" && weekDay === "*";
  const isWeekDay = weekDay !== "*" || isEveryDay;

  /**
   * We assume it can not be both at once
   */
  const day = isEveryDay
    ? "*"
    : isWeekDay
      ? `weekday-${weekDay}`
      : `monthday-${monthDay}`;

  const setHour = (_hour: string) => {
    const next = [...state];
    next[HOUR] = _hour;
    onChange(next.join(" "));
  };

  const setMonth = (_month: string) => {
    const next = [...state];
    next[MONTH] = _month;
    onChange(next.join(" "));
  };

  const setWeekDay = (_weekDay: string) => {
    const next = [...state];
    next[DAY_OF_WEEK] = _weekDay;
    next[DAY] = "*";
    onChange(next.join(" "));
  };

  const setMonthDay = (_monthDay: string) => {
    const next = [...state];
    next[DAY] = _monthDay;
    next[DAY_OF_WEEK] = "*";
    onChange(next.join(" "));
  };

  const setDay = (_day: string) => {
    if (_day === "*") {
      const next = [...state];
      next[DAY] = "*";
      next[DAY_OF_WEEK] = "*";
      return onChange(next.join(" "));
    }
    const [type, day] = _day.split("-");
    if (type === "weekday") return setWeekDay(day);
    else return setMonthDay(day);
  };

  return (
    <FormInputContainer label={label} description={description}>
      <div className="flex flex-wrap gap-2">
        <DaySelector value={day} onChange={setDay} />
        <MonthSelector
          value={month}
          onChange={setMonth}
          isWeekDay={isWeekDay}
        />
        <HourSelector value={hour} onChange={setHour} />
      </div>
    </FormInputContainer>
  );
}

function MonthSelector({
  value,
  onChange,
  isWeekDay,
}: SelectorProps & { isWeekDay: boolean }) {
  const t = useTranslation();
  const months = useMonths();

  return (
    <div className="inline">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="*">{t("cron.ofEachMonth")}</SelectItem>
          {months.map((month, index) => (
            <SelectItem key={index} value={`${index + 1}`}>
              {isWeekDay
                ? t("cron.monthWithWeekDay", { month })
                : t("cron.monthWithoutWeekDay", { month })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function HourSelector({ value, onChange }: SelectorProps) {
  const t = useTranslation();
  return (
    <div className="inline">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="*">{t("cron.everyHours")}</SelectItem>
          {[...Array.from({ length: 24 })].map((_, hour) => (
            <SelectItem key={hour} value={`${hour}`}>
              {t("cron.atTime", {
                time: t(`cron.hours.${hour.toString() as ValidHourNumber}`),
              })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type SelectorProps = {
  value: string;
  onChange: (value: string) => void;
};

function DaySelector({ value, onChange }: SelectorProps) {
  const weekDays = useWeekDays();
  const t = useTranslation();

  return (
    <div className="inline">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="*">{t("cron.everyDay")}</SelectItem>
          {weekDays.map((weekDay, index) => (
            <SelectItem key={index} value={`weekday-${index + 1}`}>
              {t("cron.everyWeekday", { day: weekDay })}
            </SelectItem>
          ))}
          {[...Array.from({ length: 31 })].map((_, day) => (
            <SelectItem key={day} value={`monthday-${day + 1}`}>
              {t("cron.everyMonthday", { day: day + 1 })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function useWeekDays() {
  const t = useTranslation();

  return [
    t("cron.weekDays.sunday"),
    t("cron.weekDays.monday"),
    t("cron.weekDays.tuesday"),
    t("cron.weekDays.wednesday"),
    t("cron.weekDays.thursday"),
    t("cron.weekDays.friday"),
    t("cron.weekDays.saturday"),
  ];
}

function useMonths() {
  const t = useTranslation();
  return [
    t("cron.months.january"),
    t("cron.months.february"),
    t("cron.months.march"),
    t("cron.months.april"),
    t("cron.months.may"),
    t("cron.months.june"),
    t("cron.months.july"),
    t("cron.months.august"),
    t("cron.months.september"),
    t("cron.months.october"),
    t("cron.months.november"),
    t("cron.months.december"),
  ];
}
